import React from "react";
import { Avatar, Box, Button, Card, CardContent, Chip, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import DiamondIcon from "@mui/icons-material/Diamond";
import EditIcon from "@mui/icons-material/Edit";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate } from "react-router-dom";
import { Customer } from "../../../../types/Customer";
import type { PersonWaitingUpdate, PersonWaitingUpdateDetails } from "../../../../hooks/Clients/dashboard";
import { toArray, type GroupMemberRaw, type AccountRaw, type BadgeRaw, type IdentifierRaw } from "./ui";
import { useBrandConfig } from "../../../../context/BrandConfigContext";

const WAITING_UPDATE_DETAIL_KEYS: (keyof PersonWaitingUpdateDetails)[] = [
    "COMM_POSTAL_ADDRESS",
    "COMM_EMAIL",
    "COMM_CELL_PHONE",
    "MOVE_ENTITY",
    "PER_ACCOUNT_MISC_MOVE",
    "PER_PERSON_IDENTIFIER",
    "PER_PERSON",
    "FORM_ANSWERED_FORM",
    "TRA_MAN_TRANSAC",
];

interface DashboardHeaderProps {
    customer: Customer;
    fullName: string;
    cardNumber: string | null;
    // Absent sans da.customer.change_detail : le bouton Modifier est masqué
    handleOpenEdit?: () => void;
    waitingUpdate?: PersonWaitingUpdate | null;
    t: (key: string) => string;
    accounts: AccountRaw[];
    mainAccount: AccountRaw | null;
    workspaceVolumeName?: string;
    npai?: boolean;
    npaiDate?: string | null;
    addressInvalid?: boolean;
    deceased?: boolean;
    emailInvalid?: boolean;
    emailInvalidDate?: string | null;
    mobileInvalid?: boolean;
    mobileInvalidDate?: string | null;
    phoneInvalid?: boolean;
    phoneInvalidDate?: string | null;
}

const isAccountDiamond = (acc: Record<string, unknown>, isGuildesWorkspace: boolean): boolean => {
    const name = ((acc.ACCOUNT_HEAD_NAME ?? acc.NAME ?? "") as string).toLowerCase();
    const unit = ((acc.UNIT ?? acc.ACCOUNT_HEAD_UNIT ?? "") as string).toLowerCase();
    return isGuildesWorkspace && (name.includes("diamant") || name.includes("diamond") || unit.includes("diamant") || unit.includes("diamond"));
};

const DashboardHeader = ({
    customer, fullName, cardNumber, handleOpenEdit, t, accounts, mainAccount, workspaceVolumeName,
    npai, npaiDate, addressInvalid, deceased, emailInvalid, emailInvalidDate, mobileInvalid, mobileInvalidDate, phoneInvalid, phoneInvalidDate,
}: DashboardHeaderProps) => {
    const navigate = useNavigate();
    const groupMembers = toArray<GroupMemberRaw>((customer as Record<string, unknown>).GROUP_MEMBERS);
    const personId = groupMembers[0]?.PERSON_ID ?? customer.PERSONID;

    const statistic = (customer as Record<string, unknown>).PER_PERSON_STATISTIC as { NAME?: string } | null;
    const mainIdentifiers = (customer as Record<string, unknown>).MAIN_IDENTIFIERS as Record<string, unknown> | null;
    const mainCard = mainIdentifiers?.MAIN_CARD as Record<string, unknown> | null;
    const allIdentifiers = toArray<IdentifierRaw>((customer as Record<string, unknown>).PER_PERSON_IDENTIFIER as IdentifierRaw[]);
    const mainIdentifier = allIdentifiers.find(id => id.PRIORITY === "1") ?? allIdentifiers[0] ?? null;
    const cardActivationDate = (mainCard?.ACTIVATION_DATE ?? mainIdentifier?.ACTIVATION_DATE ?? null) as string | null;
    const { ready: brandReady, setting } = useBrandConfig();
    // Badges et segments seulement si l'enseigne a le module (module.badges.active)
    const badgesEnabled = brandReady && setting('module.badges.active');
    const segmentBadges = badgesEnabled
        ? toArray<BadgeRaw>((customer as Record<string, unknown>).BADGES as BadgeRaw[]).filter(b => b.CUSTOMER_VISIBLE === "1" && !b.IMG)
        : [];
    // « N° client » : identifiant du type déclaré par l'enseigne (identifiers.unique.id.list)
    const uniqueIdTypes = setting('identifiers.unique.id.list');
    const customerNumber = brandReady
        ? allIdentifiers.find(id => uniqueIdTypes.includes(String(id.PERSON_IDENTIFIER_TYPE_ID)))?.IDENTIFIER_VALUE
        : undefined;

    const isGuildes = workspaceVolumeName
        ? workspaceVolumeName.toLowerCase().includes("guildes") || workspaceVolumeName.toLowerCase().includes("guilde")
        : false;

    // Realtime accounts take priority (WS_ACCOUNTS), then regular accounts, then mainAccount fallback
    const wsAccounts = (customer as Record<string, unknown>).WS_ACCOUNTS;
    const wsArr = Array.isArray(wsAccounts) ? wsAccounts as { VALUE?: string | number; NAME?: string; UNIT?: string; ACCOUNT_HEAD_NAME?: string; ACCOUNT_HEAD_UNIT?: string }[] : null;
    const displayAccounts: Record<string, unknown>[] = wsArr
        ? wsArr as Record<string, unknown>[]
        : accounts.length > 0
            ? accounts as unknown as Record<string, unknown>[]
            : mainAccount
                ? [mainAccount as unknown as Record<string, unknown>]
                : [];

    const statusTags = [
        { key: "npai", show: !!npai, label: "NPAI", date: npaiDate },
        { key: "address-invalid", show: !!addressInvalid, label: "Adresse invalide", date: npaiDate },
        { key: "deceased", show: !!deceased, label: "Décès", date: npaiDate },
        { key: "email-invalid", show: !!emailInvalid, label: "Email invalide", date: emailInvalidDate },
        { key: "mobile-invalid", show: !!mobileInvalid, label: "Mobile invalide", date: mobileInvalidDate },
        { key: "phone-invalid", show: !!phoneInvalid, label: "Téléphone invalide", date: phoneInvalidDate },
    ].filter(tag => tag.show);

    return (
        <Box sx={{ mb: 3 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/clients/search")}
                variant="text"
                size="small"
                sx={{ mb: 1.5, color: "text.secondary" }}
            >
                {t("pages.customers.dashboard.back")}
            </Button>
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Avatar sx={{ width: 52, height: 52, bgcolor: "primary.main", fontSize: "1.35rem", fontWeight: 700 }}>
                            {(customer.FIRSTNAME?.[0] || customer.LASTNAME?.[0] || "?").toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" fontWeight={700} color="text.primary">{fullName}</Typography>
                            <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap", alignItems: "center" }}>

                                {cardNumber && (
                                    <Chip
                                        icon={<CreditCardIcon sx={{ fontSize: "14px !important", color: "primary.main" }} />}
                                        label={cardNumber}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                )}
                                {customerNumber && (
                                    <Chip
                                        label={`${t("pages.customers.dashboard.customerNumber")} ${customerNumber}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                                {personId && (
                                    <Chip
                                        icon={<PersonIcon sx={{ fontSize: "14px !important" }} />}
                                        label={`ID ${personId}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderColor: "divider" }}
                                    />
                                )}
                                {statistic?.NAME && (
                                    <Chip
                                        label={`Segment : ${statistic.NAME}`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ fontWeight: 600, "& .MuiChip-label": { fontSize: "0.75rem" } }}
                                    />
                                )}
                                {segmentBadges.map((b, i) => (
                                    <Chip
                                        key={i}
                                        label={`Segment : ${b.SEGMENT_NAME || b.CATEGORY_NAME}`}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ fontWeight: 600, "& .MuiChip-label": { fontSize: "0.75rem" } }}
                                    />
                                ))}
                                {cardActivationDate && (
                                    <Chip
                                        icon={<CreditScoreIcon sx={{ fontSize: "14px !important" }} />}
                                        label={`Carte activée le ${cardActivationDate}`}
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        sx={{ "& .MuiChip-label": { fontSize: "0.75rem" } }}
                                    />
                                )}
                                {statusTags.map(tag => (
                                    <Tooltip key={tag.key} title={tag.date ? `${tag.label} depuis le ${tag.date}` : ""} arrow>
                                        <Chip
                                            label={tag.label}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                bgcolor: "background.paper",
                                                color: "error.main",
                                                borderColor: "error.main",
                                                fontWeight: 700,
                                                cursor: "default",
                                                "& .MuiChip-label": { fontSize: "0.75rem" },
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                                {customer.SUBSCRIBE_DATE && (
                                    <Chip
                                        icon={<CalendarMonthIcon sx={{ fontSize: "14px !important" }} />}
                                        label={`Activé le ${customer.SUBSCRIBE_DATE as string}`}
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        sx={{ "& .MuiChip-label": { fontSize: "0.75rem" } }}
                                    />
                                )}
                                {displayAccounts.map((acc, idx) => {
                                    const value = acc.VALUE;
                                    if (value == null) return null;
                                    const rawUnit = (acc.UNIT ?? acc.ACCOUNT_HEAD_UNIT ?? "") as string;
                                    // Masquer les comptes dont l'unité est corrompue (underscore, espace, ou trop longue)
                                    if (rawUnit && (rawUnit.includes("_") || rawUnit.includes(" ") || rawUnit.length > 12)) return null;
                                    const unit = rawUnit || "pts";
                                    const diamond = isAccountDiamond(acc, isGuildes);
                                    return (
                                        <Chip
                                            key={idx}
                                            icon={diamond
                                                ? <DiamondIcon sx={{ fontSize: "14px !important", color: "info.main" }} />
                                                : <StarIcon sx={{ fontSize: "14px !important", color: "error.main" }} />
                                            }
                                            label={`${value} ${unit}`}
                                            size="small"
                                            variant="filled"
                                            sx={{
                                                bgcolor: diamond ? "info.50" : "background.paper",
                                                color: diamond ? "info.dark" : "text.primary",
                                                border: "1px solid",
                                                borderColor: diamond ? "info.200" : "divider",
                                                fontWeight: 700,
                                                "& .MuiChip-label": { fontSize: "0.75rem" },
                                                "& .MuiChip-icon": { color: diamond ? "info.main" : "error.main" },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        {handleOpenEdit && (
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                size="small"
                                onClick={handleOpenEdit}
                                sx={{ flexShrink: 0 }}
                            >
                                {t("common.modify")}
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardHeader;
